import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import batchService from '../../../../services/mentor/batchService';
import courseService from '../../../../services/courseService';
import type { IBatch } from '../../../../types/batch';
import type { ICourse } from '../../../../types/course';
import Loader from '../../../../components/common/Loader';
import FormField from '../../../../components/common/FormField';
import { showToast } from '../../../../utils/showToast';
import { Plus, Trash2, Eye, Edit, Download, Search, SlidersHorizontal, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const Modal = lazy(() => import('../../../../components/common/Modal'));

const createBatchSchema = Yup.object().shape({
    name: Yup.string().required('Batch name is required'),
    courseId: Yup.string().required('Course is required'),
    month: Yup.string().required('Month is required'),
    year: Yup.number().typeError('Year must be a number').required('Year is required').min(2020),
    capacity: Yup.number().typeError('Capacity must be a number').min(1).optional(),
    startDate: Yup.date().typeError('Invalid date').required('Start Date is required'),
    endDate: Yup.date().typeError('Invalid date').required('End Date is required').min(Yup.ref('startDate'), 'End Date must be after Start Date'),
});

const ManageBatches: React.FC = () => {
    const { user, isLoading: isAuthLoading } = useAuth();
    const navigate = useNavigate();
    const [allBatches, setAllBatches] = useState<IBatch[]>([]);
    const [filteredBatches, setFilteredBatches] = useState<IBatch[]>([]);
    const [courses, setCourses] = useState<ICourse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState<string | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingBatch, setEditingBatch] = useState<IBatch | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [courseFilter, setCourseFilter] = useState('all');

    const fetchAllData = async () => {
        setIsLoading(true);
        try {
            const [batchesData, coursesData] = await Promise.all([
                batchService.getAllBatches(),
                courseService.getAllCoursesForAdmin(),
            ]);
            setAllBatches(batchesData);
            setCourses(coursesData);
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthLoading && user) {
            fetchAllData();
        }
    }, [isAuthLoading, user]);

    useEffect(() => {
        let filtered = allBatches;
        if (courseFilter !== 'all') {
            filtered = filtered.filter(batch => {
                if (typeof batch.course === 'object' && batch.course?._id) {
                    return batch.course._id === courseFilter;
                }
                if (typeof batch.course === 'string') {
                    return batch.course === courseFilter;
                }
                return false;
            });
        }
        if (searchTerm) {
            filtered = filtered.filter(batch => batch.name.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        setFilteredBatches(filtered);
    }, [searchTerm, courseFilter, allBatches]);

    // ✅ FIX: Restored the complete Formik configuration
    const createBatchFormik = useFormik({
        initialValues: { name: '', courseId: '', month: '', year: new Date().getFullYear(), capacity: undefined as number | undefined, startDate: '', endDate: '' },
        validationSchema: createBatchSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                await batchService.createBatch(values);
                showToast('Batch created successfully!', { type: 'success' });
                fetchAllData();
                resetForm();
                setIsCreateModalOpen(false);
            } catch (err: any) {
                showToast(err.message, { type: 'error' });
            } finally {
                setSubmitting(false);
            }
        },
    });

    // ✅ FIX: Restored the complete Formik configuration
    const editBatchFormik = useFormik({
        initialValues: {
            name: editingBatch?.name || '',
            startDate: editingBatch ? new Date(editingBatch.startDate).toISOString().split('T')[0] : '',
            endDate: editingBatch ? new Date(editingBatch.endDate).toISOString().split('T')[0] : '',
            capacity: editingBatch?.capacity || undefined,
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().required('Batch name is required'),
            capacity: Yup.number().min(1).optional(),
            startDate: Yup.date().required('Start Date is required'),
            endDate: Yup.date().required('End Date is required').min(Yup.ref('startDate'), 'End must be after Start'),
        }),
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting }) => {
            if (!editingBatch) return;
            setIsProcessing(editingBatch._id);
            try {
                await batchService.updateBatch(editingBatch._id, values);
                showToast('Batch updated successfully!', { type: 'success' });
                fetchAllData();
                setIsEditModalOpen(false);
            } catch (err: any) {
                showToast(err.message, { type: 'error' });
            } finally {
                setSubmitting(false);
                setIsProcessing(null);
            }
        },
    });

    const handleDeleteBatch = async (batch: IBatch) => {
        if (!window.confirm(`Are you sure you want to delete the batch "${batch.name}"?`)) return;
        setIsProcessing(batch._id);
        try {
            await batchService.deleteBatch(batch._id);
            showToast(`Batch "${batch.name}" deleted.`, { type: 'success' });
            fetchAllData();
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsProcessing(null);
        }
    };

    const handleExportBatch = async (batchId: string) => {
        setIsProcessing(batchId);
        try {
            showToast('Generating your export...', { type: 'info' });
            await batchService.exportBatch(batchId, 'excel');
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsProcessing(null);
        }
    };

    const handleOpenEditModal = (batch: IBatch) => {
        setEditingBatch(batch);
        setIsEditModalOpen(true);
    };

    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    if (isAuthLoading || isLoading) return <Loader />;
    
    const renderActionButtons = (batch: IBatch) => (
        <div className="flex justify-end items-center gap-1">
            <button 
                onClick={() => batch.course && navigate(`/dashboard/mentor/batches/${batch._id}`)} 
                disabled={!batch.course || !!isProcessing} 
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed" title="View Students"
            >
                <Eye size={18} />
            </button>
            <button onClick={() => handleOpenEditModal(batch)} disabled={!!isProcessing} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full" title="Edit Batch">
                <Edit size={18} />
            </button>
            <button onClick={() => handleExportBatch(batch._id)} disabled={isProcessing === batch._id} className="p-2 text-green-600 hover:bg-green-100 rounded-full" title="Export Roster (Excel)">
                <Download size={18} />
            </button>
            {user?.isAdmin && (
                <button onClick={() => handleDeleteBatch(batch)} disabled={isProcessing === batch._id} className="p-2 text-red-600 hover:bg-red-100 rounded-full" title="Delete Batch">
                    <Trash2 size={18} />
                </button>
            )}
        </div>
    );
    
    return (
        <>
            <Helmet>
                <title>Manage Batches | SGC Dashboard</title>
            </Helmet>
            <div className="space-y-6">
                <header className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Batch Management</h1>
                            <p className="text-gray-500 mt-1">Create, view, and manage all course batches.</p>
                        </div>
                        <button onClick={() => setIsCreateModalOpen(true)} className="w-full md:w-auto bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2">
                            <Plus size={16} /> Create New Batch
                        </button>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 mt-4 pt-4 border-t">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input type="text" placeholder="Search by batch name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-11 pr-4 py-2.5 border border-gray-300 rounded-full bg-gray-50 focus:ring-2 focus:ring-blue-500 transition" />
                        </div>
                        <div className="relative w-full sm:w-60">
                            <SlidersHorizontal className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} className="w-full pl-11 pr-4 py-2.5 border border-gray-300 rounded-full bg-gray-50 focus:ring-2 focus:ring-blue-500 transition appearance-none">
                                <option value="all">All Courses</option>
                                {courses.map(course => <option key={course._id} value={course._id}>{course.title}</option>)}
                            </select>
                        </div>
                    </div>
                </header>

                {filteredBatches.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-2xl shadow-sm border">
                        <Layers className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-lg font-medium text-gray-900">No Batches Found</h3>
                        <p className="mt-1 text-sm text-gray-500">{searchTerm || courseFilter !== 'all' ? 'No batches match your criteria.' : 'Click "Create New Batch" to get started.'}</p>
                    </div>
                ) : (
                    <>
                        <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Batch Name</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Course</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Students</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredBatches.map(batch => {
                                        const course = batch.course && typeof batch.course === 'object' ? batch.course : null;
                                        return (
                                            <tr key={batch._id}>
                                                <td className="px-6 py-4 font-medium text-gray-900">{batch.name}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{course ? course.title : <span className="text-red-500">Course Deleted</span>}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{batch.enrollments.length} / {batch.capacity || '∞'}</td>
                                                <td className="px-6 py-4 text-right">{renderActionButtons(batch)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="md:hidden space-y-4">
                            {filteredBatches.map(batch => {
                                const course = batch.course && typeof batch.course === 'object' ? batch.course : null;
                                return (
                                    <div key={batch._id} className="bg-white p-4 rounded-2xl shadow-sm border space-y-4">
                                        <div>
                                            <p className="font-bold text-gray-900">{batch.name}</p>
                                            <p className="text-sm text-gray-500">{course ? course.title : 'Course Deleted'}</p>
                                        </div>
                                        <div className="border-t pt-4 flex justify-between items-center">
                                            <p className="text-sm font-medium text-gray-700">Students: <span className="font-bold">{batch.enrollments.length} / {batch.capacity || '∞'}</span></p>
                                            {renderActionButtons(batch)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
            
            <Suspense fallback={null}>
                <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Create New Batch">
                    <form onSubmit={createBatchFormik.handleSubmit} className="space-y-4">
                        <FormField label="Batch Name" name="name" type="text" value={createBatchFormik.values.name} onChange={createBatchFormik.handleChange} onBlur={createBatchFormik.handleBlur} error={createBatchFormik.touched.name ? createBatchFormik.errors.name : undefined}/>
                        <div>
                            <label htmlFor="courseId" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
                            <select id="courseId" name="courseId" value={createBatchFormik.values.courseId} onChange={createBatchFormik.handleChange} onBlur={createBatchFormik.handleBlur} className={`w-full px-3 py-2 border rounded-md shadow-sm ${createBatchFormik.touched.courseId && createBatchFormik.errors.courseId ? 'border-red-500' : 'border-gray-300'}`}>
                                <option value="">-- Select Course --</option>
                                {courses.map(course => (<option key={course._id} value={course._id}>{course.title}</option>))}
                            </select>
                            {createBatchFormik.touched.courseId && createBatchFormik.errors.courseId && (<span className="text-xs text-red-600">{createBatchFormik.errors.courseId}</span>)}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                                <select id="month" name="month" value={createBatchFormik.values.month} onChange={createBatchFormik.handleChange} onBlur={createBatchFormik.handleBlur} className={`w-full px-3 py-2 border rounded-md shadow-sm ${createBatchFormik.touched.month && createBatchFormik.errors.month ? 'border-red-500' : 'border-gray-300'}`}>
                                    <option value="">-- Select Month --</option>
                                    {months.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                                {createBatchFormik.touched.month && createBatchFormik.errors.month && (<span className="text-xs text-red-600">{createBatchFormik.errors.month}</span>)}
                            </div>
                            <FormField label="Year" name="year" type="number" value={String(createBatchFormik.values.year)} onChange={createBatchFormik.handleChange} onBlur={createBatchFormik.handleBlur} error={createBatchFormik.touched.year ? createBatchFormik.errors.year : undefined}/>
                        </div>
                        <FormField label="Capacity (Optional)" name="capacity" type="number" value={String(createBatchFormik.values.capacity || '')} onChange={createBatchFormik.handleChange} onBlur={createBatchFormik.handleBlur} error={createBatchFormik.touched.capacity ? createBatchFormik.errors.capacity : undefined}/>
                        <div className="grid grid-cols-2 gap-4">
                            <FormField label="Start Date" name="startDate" type="date" value={createBatchFormik.values.startDate} onChange={createBatchFormik.handleChange} onBlur={createBatchFormik.handleBlur} error={createBatchFormik.touched.startDate ? createBatchFormik.errors.startDate : undefined}/>
                            <FormField label="End Date" name="endDate" type="date" value={createBatchFormik.values.endDate} onChange={createBatchFormik.handleChange} onBlur={createBatchFormik.handleBlur} error={createBatchFormik.touched.endDate ? createBatchFormik.errors.endDate : undefined}/>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <button type="button" onClick={() => setIsCreateModalOpen(false)} className="px-4 py-2 text-sm font-medium bg-gray-200 rounded-md hover:bg-gray-300" disabled={createBatchFormik.isSubmitting}>Cancel</button>
                            <button type="submit" disabled={createBatchFormik.isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center justify-center min-w-[120px]">
                                {createBatchFormik.isSubmitting ? <Loader /> : <span>Create Batch</span>}
                            </button>
                        </div>
                    </form>
                </Modal>
                
                {editingBatch && (
                    <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title={`Edit Batch: ${editingBatch.name}`}>
                        <form onSubmit={editBatchFormik.handleSubmit} className="space-y-4">
                             <FormField label="Batch Name" name="name" type="text" value={editBatchFormik.values.name} onChange={editBatchFormik.handleChange} onBlur={editBatchFormik.handleBlur} error={editBatchFormik.touched.name ? editBatchFormik.errors.name : undefined}/>
                             <FormField label="Capacity (Optional)" name="capacity" type="number" value={String(editBatchFormik.values.capacity || '')} onChange={editBatchFormik.handleChange} onBlur={editBatchFormik.handleBlur} error={editBatchFormik.touched.capacity ? editBatchFormik.errors.capacity : undefined}/>
                            <div className="grid grid-cols-2 gap-4">
                                <FormField label="Start Date" name="startDate" type="date" value={editBatchFormik.values.startDate} onChange={editBatchFormik.handleChange} onBlur={editBatchFormik.handleBlur} error={editBatchFormik.touched.startDate ? editBatchFormik.errors.startDate : undefined}/>
                                <FormField label="End Date" name="endDate" type="date" value={editBatchFormik.values.endDate} onChange={editBatchFormik.handleChange} onBlur={editBatchFormik.handleBlur} error={editBatchFormik.touched.endDate ? editBatchFormik.errors.endDate : undefined}/>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-4 py-2 text-sm font-medium bg-gray-200 rounded-md hover:bg-gray-300" disabled={editBatchFormik.isSubmitting}>Cancel</button>
                                <button type="submit" disabled={editBatchFormik.isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center justify-center min-w-[120px]">
                                    {editBatchFormik.isSubmitting ? <Loader /> : <span>Update Batch</span>}
                                </button>
                            </div>
                        </form>
                    </Modal>
                )}
            </Suspense>
        </>
    );
};

export default ManageBatches;